/**
 * Widget Catalog Dialog (issue #450, UJ-DASH-15/16/18; issue #1260, UJ-DASH-1260-*)
 *
 * "Add widget" picker shown in the dashboard's Configure edit mode. Lists the
 * role-filtered `availableWidgets` catalog (#449). Per issue #1260/
 * [ADR 1260](../../../../documents/decisions/1260-multi-instance-widgets-instanceid-identity.md),
 * every catalog widget is multi-instance-eligible, so selecting an entry
 * already on the dashboard adds a new, independently-configurable instance
 * rather than being excluded from the list.
 */

import { useRef, useEffect } from 'react';
import type { AvailableWidget } from '../../types/preferences';
import '@tylertech/forge/dialog';
import '@tylertech/forge/list';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import './WidgetCatalogDialog.css';

export interface WidgetCatalogDialogProps {
  open: boolean;
  /** The role-filtered widget catalog — always the full list (issue #1260). */
  addableWidgets: AvailableWidget[];
  onAdd: (widget: AvailableWidget) => void;
  onClose: () => void;
}

export function WidgetCatalogDialog({
  open,
  addableWidgets,
  onAdd,
  onClose,
}: WidgetCatalogDialogProps): JSX.Element {
  const dialogRef = useRef<HTMLElement & { open: boolean }>(null);

  const DialogElement = 'forge-dialog' as React.ElementType;
  const ListElement = 'forge-list' as React.ElementType;
  const ListItemElement = 'forge-list-item' as React.ElementType;
  const IconElement = 'forge-icon' as React.ElementType;
  const ForgeButton = 'forge-button' as React.ElementType;

  useEffect(() => {
    if (dialogRef.current) {
      dialogRef.current.open = open;
    }
  }, [open]);

  return (
    <DialogElement
      ref={dialogRef}
      onForge-dialog-close={onClose}
      className="widget-catalog-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="widget-catalog-title"
    >
      <div className="widget-catalog-dialog__content">
        <h2 id="widget-catalog-title" className="forge-typography--headline5">
          Add Widget
        </h2>

        {addableWidgets.length === 0 ? (
          <p className="forge-typography--body2 widget-catalog-dialog__empty">
            No widgets are available to add.
          </p>
        ) : (
          <ListElement className="widget-catalog-dialog__list">
            {addableWidgets.map((widget) => (
              <ListItemElement
                key={widget.id}
                className="widget-catalog-dialog__item"
                onClick={() => onAdd(widget)}
                role="button"
                tabIndex={0}
              >
                <IconElement name="add" aria-hidden="true" slot="leading" />
                <span className="forge-typography--label1">{widget.title}</span>
              </ListItemElement>
            ))}
          </ListElement>
        )}

        <div className="widget-catalog-dialog__actions">
          <ForgeButton type="button" variant="text" onClick={onClose}>
            Close
          </ForgeButton>
        </div>
      </div>
    </DialogElement>
  );
}
